/*!
Copyright (C) 2022 Liberty Infrasystems. All rights reserved.
*/

// attempt -> output:
// 0 1 2 3 4 5...
// 0 0 0 0 0 0...
function nullBackoff() {
    return 0;
}

// attempt -> output:
// 0 1 2 3 4 5...
// 1 1 1 1 1 1...
function constantBackoff() {
    return 1;
}

// attempt -> output:
// 0 1 2 3 4 5...
// 1 2 3 4 5 6...
function linearBackoff(n) {
    return n+1;
}

// attempt -> output:
// 0 1 2 3 4 5 6  7 ...
// 1 1 2 3 5 8 13 21...
// returns the numbers in a fibonacci sequence, NOT their sum
function fibonacciBackoff(n) {
    if (n === 0) {
        return 1;
    }
    let last = 0;
    let current = 1;
    for (let i = 0; i < n; i += 1) {
        const sum = last + current;
        last = current;
        current = sum;
    }
    return current;
}

// attempt -> output:
// 0 1 2 3  4  5...
// 1 4 9 16 25 36...
// NOTE: the square output is shifted compared to what you might expect, because we want a backoff after the first attempt so the output should start with 1*1, then 2*2, etc. and not start with 0*0
function squareBackoff(n) {
    const np = n + 1;
    return np * np;
}

// attempt -> output:
// 0 1 2 3 4  5...
// 1 2 4 8 16 32...
function exponentialBackoff(n) {
    return 2 ** n;
}

/**
 * Computes a backoff amount given the attempt number (0, 1, 2, ...) and
 * a backoff algorithm name ('linear', 'fibonacci', 'square', 'exponential').
 *
 * @param {*} n index of the backoff amount to return
 * @param {*} options { alg } name of backoff algorithm to use; default is 'exponential'
 * @returns an integer representing the amount of time to wait before the next request; result should be multiplied by a time unit such as 1000ms, for example result of 2 would mean 2 * 1000ms = wait 2 seconds
 */
function backoff(n, options = {}) {
    const { alg = 'exponential' } = options;
    let fn;
    switch (alg) {
    case 'none':
        fn = nullBackoff;
    case 'constant':
        fn = constantBackoff;
        break;
    case 'linear':
        fn = linearBackoff;
        break;
    case 'fibonacci':
        fn = fibonacciBackoff;
        break;
    case 'square':
        fn = squareBackoff;
        break;
    case 'exponential':
        fn = exponentialBackoff;
        break;
    case 'custom':
        fn = options.custom;
        break;
    default:
        fn = nullBackoff;
        break;
    }
    const result = fn(n);
    // console.log(`backoff(${alg}, ${n}) = ${result}`);
    return result;
}

/**
 * Randomizes the output of `backoff` within a specified range or with the default
 * range of [0.5n, 1.5n). This means if the `backoff` algorithm returns a 1, the
 * output from randomizedBackoff could be as low as 0.5 or as high as 1.49999.
 * 
 * This is useful to avoid the "thundering herd" problem in a distributed system
 * when many clients are connecting to a server, or where many applications are
 * attempting to write to storage, so that even when they are configured with the
 * same backoff algorithm they will not all retry their attempts at the same time.
 * 
 * @param {*} n index of the backoff amount to return
 * @param {*} options { alg, min, max } name of backoff algorithm to use; default is 'exponential'; min (inclusive) and max (exclusive) represent the range [min, max) to use when randomizing each value, with a default of min=0.5 and max=1.5
 * @returns a dimensionless number representing the amount of time to wait before the next request; result should be interpreted using a time unit such milliseconds or seconds
 */
function randomizedBackoff(n, options = {}) {
    const { min = 0.5, max = 1.5 } = options;
    const r = (Math.random() * (max - min)) + min;
    return backoff(n, options) * r;
}

export {
    backoff,
    randomizedBackoff,
};
